import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  Card,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, StudentGrade, StudentGradeEditResource, StudentGradeViewResource } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { StudentGradePageBody } from "./StudentGradePageBody";
import { StudentGradePageHeader } from "./StudentGradePageHeader";

const routeName = "studentGrade";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  studentId?: number = 0;
  schoolYear?: number = 0;
  schoolId?: number = 0;
  termTypeId?: number = 0;
  termNumber?: number = 0;
}

export const studentGradePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "Grades",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export const StudentGradePage = () => {
  const ROOT_PERMISSION: Permission = Permission.StudentGrade;
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const navigate = useNavigate();
  const [grade, setGrade] = useState<StudentGrade>({} as StudentGrade);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [grade] });
  const [viewResource, setViewResource] = useState<StudentGradeViewResource>({});
  const [editResource, setEditResource] = useState<StudentGradeEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  const [studentId] = useState<number | undefined>(optionalParams.studentId);
  const [schoolYear] = useState<number | undefined>(optionalParams.schoolYear);
  const [schoolId] = useState<number | undefined>(optionalParams.schoolId);
  const [termTypeId] = useState<number | undefined>(optionalParams.termTypeId);
  const [termNumber] = useState<number | undefined>(optionalParams.termNumber);

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(
      `../${studentGradePageRoute.getPath({
        id: id,
        crudMode: crudMode,
        screenType: params.screenType,
      })}`
    );

  const fetch = (id: number) => {
    ApiService.studentGrades.get(id).then((response) => {
      if (id === 0 && studentId) response.studentId = studentId;

      if (id === 0) {
        if (schoolYear) response.year = schoolYear;
        if (schoolId) response.schoolId = schoolId;
        if (termTypeId) response.termTypeId = termTypeId;
        if (termNumber) response.termNumber = termNumber;
      }

      setGrade(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.studentGrades.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.studentGrades.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  const pageHeader = (
    <StudentGradePageHeader
      rootPermission={ROOT_PERMISSION}
      handle={studentGradePageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      grade={grade}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
    />
  );

  const pageBody = (
    <StudentGradePageBody
      crudMode={params.crudMode}
      grade={grade}
      setGrade={setGrade}
      viewResource={viewResource}
      editResource={editResource}
      validationErrors={validationErrors}
    />
  );

  return params.screenType === "standard" ? (
    <Page isForm header={pageHeader}>
      <Card>{pageBody}</Card>
    </Page>
  ) : (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
